import { Plural, T, Var } from 'gt-react';
import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';

import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { suspendedRowKey } from '@/lib/daily-reports/flaggedSuspendedItems';
import type {
  DailyReportFileResult,
  DailyReportSummary,
  FlaggedSuspendedRow,
} from '@/lib/daily-reports/types';
import { useDailyReportKindLabel } from './dailyReportLabels';
import { ReasonChips } from './ReasonChips';
import type { PendingDailyReport } from './useDailyReports';
import { SummaryStat } from '../SummaryStat';

function FileStatusBadge({ result }: { result: DailyReportFileResult }) {
  const kindLabel = useDailyReportKindLabel();
  if (result.status === 'analyzed') {
    return (
      <Badge variant="success">
        <T context="badge when a daily report was analyzed">Analyzed</T>
      </Badge>
    );
  }
  if (result.status === 'recognized') {
    return (
      <Badge variant="warning">
        <T context="badge when a known daily report is not yet analyzed">
          <Var>{kindLabel(result.kind)}</Var> — not analyzed yet
        </T>
      </Badge>
    );
  }
  if (result.status === 'unreadable') {
    return (
      <Badge variant="destructive">
        <T context="badge when a daily report file could not be parsed">
          Couldn’t read
        </T>
      </Badge>
    );
  }
  return (
    <Badge variant="secondary">
      <T context="badge when a dropped file is not a known daily report">
        Unrecognized
      </T>
    </Badge>
  );
}

export function FileResultList({
  results,
  pending,
  onRemove,
}: {
  results: DailyReportFileResult[];
  pending: PendingDailyReport[];
  onRemove: (id: string) => void;
}) {
  const kindLabel = useDailyReportKindLabel();
  if (results.length === 0 && pending.length === 0) return null;

  return (
    <ul className="flex flex-col gap-2">
      {results.map((result) => (
        <li
          key={result.id}
          className="flex items-center gap-2 rounded-lg border p-3"
        >
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {result.fileName}
          </span>
          {result.status === 'recognized' ? (
            <span className="sr-only">{kindLabel(result.kind)}</span>
          ) : null}
          <FileStatusBadge result={result} />
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => onRemove(result.id)}
          >
            <T context="button that removes one daily report file from the list">
              Remove
            </T>
          </button>
        </li>
      ))}
      {pending.map((file) => (
        <li
          key={file.key}
          role="status"
          className="flex items-center gap-2 rounded-lg border p-3"
        >
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {file.fileName}
          </span>
          <Spinner />
          <span className="text-sm text-muted-foreground">
            <T context="status beside a daily report file while it is being read and analyzed">
              Analyzing…
            </T>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function AnalysisSummary({ summary }: { summary: DailyReportSummary }) {
  if (summary.fileCount === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <SummaryStat
        label={<T context="count of daily report files dropped">Files</T>}
        value={summary.fileCount}
      />
      <SummaryStat
        label={<T context="count of daily report rows scanned">Rows scanned</T>}
        value={summary.rowsScanned}
      />
      <SummaryStat
        label={
          <T context="count of daily report rows with discrepancies">
            Discrepancies
          </T>
        }
        value={summary.flaggedCount}
      />
      <SummaryStat
        label={
          <T context="count of STOCK_V stock-without-item rows with a Vera DC">
            RD stock without item
          </T>
        }
        value={summary.rdStockMappedCount}
      />
      <SummaryStat
        label={
          <T context="count of dropped files that are a known but unsupported daily report">
            Not analyzed yet
          </T>
        }
        value={summary.recognizedCount}
      />
    </div>
  );
}

export function AllClearPanel({ rowsScanned }: { rowsScanned: number }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-green-500/30 bg-green-50/50 p-6 text-center dark:bg-green-950/20">
      <CheckCircleIcon className="h-12 w-12 text-green-600" weight="fill" />
      <p className="text-lg font-semibold">
        <T context="heading when a Suspended Item report has no discrepancies">
          No discrepancies
        </T>
      </p>
      <p className="max-w-md text-sm text-muted-foreground">
        <T context="message when every Suspended Item row is clean">
          Scanned <Var>{rowsScanned}</Var>{' '}
          <Plural n={rowsScanned} one="row" other="rows" />. No items need
          unsuspending in AS/400.
        </T>
      </p>
    </div>
  );
}

export function FlaggedRowsTable({ rows }: { rows: FlaggedSuspendedRow[] }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-50/50 p-3 text-sm dark:bg-amber-950/20">
        <WarningCircleIcon
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
          weight="fill"
        />
        <p>
          <T context="instruction after Suspended Item discrepancies are flagged">
            Unsuspend these items in AS/400 after resolving the discrepancy.
            Gently does not write back to AS/400.
          </T>
        </p>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <T context="column header for the source daily report file name">
                  File
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for DIV on a Suspended Item row">
                  DIV
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for BRANCH on a Suspended Item row">
                  Branch
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for the Vera RD DC name on a Suspended Item row">
                  DC
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for RD stock location on a Suspended Item row">
                  Stock
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for RD item-master location on a Suspended Item row">
                  Item master
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for ITEM# on a Suspended Item row">
                  Item
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for DESCRIP on a Suspended Item row">
                  Description
                </T>
              </TableHead>
              <TableHead className="text-right">
                <T context="column header for how many report rows one flagged Suspended Item covers">
                  Rows
                </T>
              </TableHead>
              <TableHead>
                <T context="column header listing why a Suspended Item row was flagged">
                  Reasons
                </T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={suspendedRowKey(row)}>
                <TableCell className="max-w-[10rem] truncate text-xs">
                  {row.fileName}
                </TableCell>
                <TableCell>{row.div}</TableCell>
                <TableCell>{row.branch}</TableCell>
                <TableCell>{row.dc}</TableCell>
                <TableCell>{row.stockLocation}</TableCell>
                <TableCell>{row.itemMasterLocation}</TableCell>
                <TableCell className="font-medium">{row.itemNumber}</TableCell>
                <TableCell className="max-w-[16rem] truncate">
                  {row.description}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.rowCount}
                </TableCell>
                <TableCell>
                  <ReasonChips reasons={row.reasons} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
